import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  disablePush,
  enablePush,
  hasPushSubscription,
  isIosWithoutInstall,
  isPushSupported,
  syncPushCopy,
} from '../services/PushNotificationService';
import { NOTIF_TITLE_KEYS } from '../constants/notificationTitles';

export interface PushNotificationsState {
  /** Whether this browser can do push at all. False means the switch should not be shown as something to try. */
  supported: boolean;
  /**
   * True on an iPhone that has not added the app to its home screen -- the one
   * case where "no puedes" is the wrong thing to say, because the honest answer
   * is "instálala y podrás". See PushNotificationService.isIosWithoutInstall.
   */
  needsHomeScreenInstall: boolean;
  /** Whether THIS device currently receives push. Not an account setting -- permission is per browser. */
  enabled: boolean;
  /** The browser's own answer. 'denied' can only be undone by the person, in browser settings: no site can re-ask. */
  permission: NotificationPermission;
  /** True while a request is in flight, so the switch can be disabled rather than double-fired. */
  busy: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  /** Non-null when the last attempt failed for a reason worth showing. 'PushNotConfigured' means this environment has no keys. */
  error: string | null;
}

/**
 * "¿Me avisas en este móvil?" -- the whole permission and subscription
 * lifecycle, as one thing a screen can render.
 *
 * It is a hook rather than a context because the answer is genuinely per
 * device and never travels with the account: the same person is subscribed on
 * their phone and not on the office desktop, and both are correct at once.
 * Anything that wants to offer this -- today the settings section, tomorrow a
 * prompt after somebody's first booking -- asks the same hook and gets the same
 * five facts.
 *
 * It also keeps the service worker's own copy of the notification titles in the
 * language currently on screen, so a push that arrives at midnight is written
 * the way the person reads the app.
 */
export const usePushNotifications = (): PushNotificationsState => {
  const { t, i18n } = useTranslation();

  const supported = isPushSupported();
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied'
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** The titles, already translated, keyed by notification type -- what the worker needs and all it needs. */
  const titles = useCallback(
    () =>
      Object.entries(NOTIF_TITLE_KEYS).reduce<Record<string, string>>((all, [type, key]) => {
        all[type] = t(key);
        return all;
      }, {}),
    [t]
  );

  useEffect(() => {
    let cancelled = false;
    hasPushSubscription().then((has) => {
      if (!cancelled) setEnabled(has);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-written on every language change, and on mount for somebody who
  // subscribed months ago: the dictionary in the cache is a snapshot, and a
  // person who switches the app to English should not keep getting Spanish on
  // their lock screen.
  useEffect(() => {
    if (!supported) return;
    if (Notification.permission !== 'granted') return;
    syncPushCopy(titles());
  }, [supported, titles, i18n.language]);

  const enable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const granted = await enablePush(titles());
      setEnabled(granted);
      setPermission(isPushSupported() ? Notification.permission : 'denied');
      // Not an error: they were asked and said no. The switch simply stays off.
    } catch (caught: any) {
      setError(caught?.message || 'PushFailed');
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }, [titles]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await disablePush();
      setEnabled(false);
    } catch (caught: any) {
      setError(caught?.message || 'PushFailed');
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    supported,
    needsHomeScreenInstall: isIosWithoutInstall(),
    enabled,
    permission,
    busy,
    enable,
    disable,
    error,
  };
};
